import { clsx } from 'clsx'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { IconCaretLeft, IconCaretRight } from '@redteclab/icons'

import { IconPause, IconPlay } from '../../../icons'
import { AD_BANNER_AUTO_REFRESH_SLIDE_ID } from '../../adServerConst'

import {
  type AdServerBannerNavigationProps,
  useAdServerBannerNavigationActions,
} from './useAdServerBannerNavigationActions'

export const AdServerBannerNavigation: FC<AdServerBannerNavigationProps> = ({
  bannerRef,
  carouselRef,
  currentIndex,
  totalCount,
}) => {
  const intl = useIntl()
  const {
    handleBlurButton,
    handleFocusButton,
    handleNextButton,
    handlePauseButton,
    handlePrevButton,
    handleResumeButton,
    isPaused,
  } = useAdServerBannerNavigationActions({
    bannerRef,
    carouselRef,
    currentIndex,
    totalCount,
  })

  const isPrevDisabled = currentIndex === 0
  const isNextDisabled = currentIndex + 1 === totalCount

  return (
    <div className="mt-2 flex flex-wrap justify-center tablet:mt-0">
      <button
        aria-controls={AD_BANNER_AUTO_REFRESH_SLIDE_ID}
        aria-label={intl.formatMessage({
          id: 'general.label.previous',
        })}
        className={clsx(
          'flex size-8 items-center justify-center rounded-full tablet:absolute tablet:-left-3.5 tablet:top-1/2 tablet:-translate-y-1/2',
          isPrevDisabled ? 'bg-light-primary-high' : 'bg-light-brand-high',
        )}
        disabled={isPrevDisabled}
        onBlur={handleBlurButton}
        onClick={handlePrevButton}
        onFocus={handleFocusButton}
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        type="button"
      >
        <IconCaretLeft className="size-4" />
      </button>
      <button
        aria-label={
          isPaused
            ? intl.formatMessage({ id: 'general.label.resume' })
            : intl.formatMessage({ id: 'general.label.pause' })
        }
        className={clsx(
          'mx-1 flex size-8 items-center justify-center rounded-full tablet:absolute tablet:bottom-2 tablet:right-2 tablet:mx-0',
          isNextDisabled ? 'bg-light-primary-high' : 'bg-light-brand-high',
        )}
        disabled={isNextDisabled}
        onBlur={handleBlurButton}
        onClick={() => {
          if (isPaused) {
            handleResumeButton()
          } else {
            handlePauseButton()
          }
        }}
        onFocus={handleFocusButton}
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        type="button"
      >
        {isPaused ? (
          <IconPlay className="size-4" />
        ) : (
          <IconPause className="size-4" />
        )}
      </button>
      <button
        aria-controls={AD_BANNER_AUTO_REFRESH_SLIDE_ID}
        aria-label={intl.formatMessage({
          id: 'general.label.next',
        })}
        className={clsx(
          'flex size-8 items-center justify-center rounded-full tablet:absolute tablet:-right-3.5 tablet:top-1/2 tablet:-translate-y-1/2',
          isNextDisabled ? 'bg-light-primary-high' : 'bg-light-brand-high',
        )}
        disabled={isNextDisabled}
        onBlur={handleBlurButton}
        onClick={handleNextButton}
        onFocus={handleFocusButton}
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        type="button"
      >
        <IconCaretRight className="size-4" />
      </button>
    </div>
  )
}
